document.addEventListener('DOMContentLoaded', () => {
  /**
   * initializeProduct function sets up the product logic
   * based on configuration data passed from the Liquid template.
   *
   * @param {Object} config - contains essential configuration and product data.
   */
  const initializeProduct = (config) => {
    const {
      sectionId, // ID of the current section in Shopify
      btnLabel, // Label for the "Add to Cart" button
      soldOutText, // Text to display if the product is sold out
      productData, // JSON object containing the product data
      checkoutRedirect, // Boolean indicating if the user should be redirected to checkout after adding to cart
    } = config;

    // DOM elements
    const productForm = document.getElementById('product-form');
    const scrollableDiv = document.getElementById(`custom-grid-product__colors-scrollable-${productForm.getAttribute('data-id')}`);

    // Store currently selected level and swatch elements
    let currentLevel = document.querySelector('.level-value.level-value-selected');
    let currentSwatch = scrollableDiv?.querySelector('.custom-swatch-selected');

    // Image elements that need to be updated when the variant or swatch changes
    const featuredImage = document.getElementById(`FeaturedImage-${sectionId}`);
    const productSinglePhoto = document.querySelector('.product-single__photo');
    const transparentImage =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAwAB/Vi2XAAAAABJRU5ErkJggg=='; // Transparent image used temporarily

    /**
     * checkAndSetInitialVariant function checks if a variant is selected
     * based on the current selected level and swatch (color) and updates
     * the form's data attributes with the correct variant ID and availability.
     *
     * This ensures that the correct variant is set as soon as the page loads.
     */
    const checkAndSetInitialVariant = () => {
      const selectedLevel = currentLevel ? currentLevel.textContent.trim() : null;
      const selectedColor = currentSwatch ? currentSwatch.getAttribute('data-color-value').trim() : null;

      // Find the correct variant based on the selected options
      const correctVariant = productData.variants.find((variant) => {
        return variant.option1 === selectedLevel && (!selectedColor || variant.option2 === selectedColor);
      });

      // If a valid variant is found, update form attributes and price details
      if (correctVariant) {
        productForm.setAttribute('data-variant-id', correctVariant.id);
        document.querySelector(`#ProductPrice-${sectionId}`).textContent = theme.Currency.formatMoney(correctVariant.price);

        // Update compare price and its visibility
        const comparePriceElement = document.querySelector(`#ComparePrice-${sectionId}`);
        comparePriceElement.textContent = correctVariant.compare_at_price
          ? theme.Currency.formatMoney(correctVariant.compare_at_price)
          : '';
        comparePriceElement.style.display =
          correctVariant.compare_at_price && correctVariant.compare_at_price > correctVariant.price ? 'inline' : 'none';

        // Update availability information
        productForm.setAttribute('data-available', correctVariant.available);
        const addToCartButton = document.getElementById(`AddToCart-${sectionId}`);
        const addToCartText = document.getElementById(`AddToCartText-${sectionId}`);

        // If the variant isn't available, disable the "Add to Cart" button and update its text
        if (!correctVariant.available) {
          addToCartButton.disabled = true;
          addToCartText.textContent = soldOutText;
        } else {
          addToCartButton.disabled = false;
          addToCartText.textContent = btnLabel;
        }
      } else {
        console.warn('No matching variant found for the initial options.');
      }
    };

    /**
     * updateProductWithSelectedOptions function is called whenever an option
     * (e.g., level or swatch) is selected by the user. This function will
     * update the product form with the correct variant ID and update the UI
     * elements with the correct product details.
     */
    const updateProductWithSelectedOptions = () => {
      checkAndSetInitialVariant();
    };

    // Run the initial variant setup when the page loads
    checkAndSetInitialVariant();

    /**
     * Event listeners for level selection.
     * When a level is clicked, it updates the current selected level,
     * updates the variant data accordingly.
     */
    const levels = document.querySelectorAll('.level-value');
    levels.forEach((level) => {
      level.addEventListener('click', () => {
        if (currentLevel) {
          currentLevel.classList.remove('level-value-selected');
        }
        level.classList.add('level-value-selected');
        currentLevel = level;

        // Update the product form and UI based on this selection
        updateProductWithSelectedOptions();
      });
    });

    /**
     * Event listeners for swatch selection.
     * When a swatch is clicked, it updates the currently selected swatch,
     * changes the displayed product image, and updates the variant.
     */
    const swatches = scrollableDiv?.querySelectorAll('.custom-color-swatch-inline');
    swatches?.forEach((swatch) => {
      const colorSwatchValue = swatch.getAttribute('data-color-value');

      // Add tooltip with color name on hover using tippy.js
      tippy(swatch, {
        content: colorSwatchValue,
        animation: 'custom',
      });

      swatch.addEventListener('click', () => {
        if (currentSwatch) {
          currentSwatch.classList.remove('custom-swatch-selected');
        }
        swatch.classList.add('custom-swatch-selected');
        currentSwatch = swatch;

        const imageUrl = swatch.getAttribute('data-image-url');
        productSinglePhoto.classList.add('fading');

        // Temporarily set the image to transparent and then load the new image
        setTimeout(() => {
          featuredImage.setAttribute('src', transparentImage);
          const tempImage = new Image();
          tempImage.src = imageUrl;
          tempImage.onload = () => {
            featuredImage.setAttribute('src', imageUrl);
            setTimeout(() => {
              productSinglePhoto.classList.remove('fading');
            }, 50);
          };
        }, 300);

        // Update the product form and UI based on this swatch selection
        updateProductWithSelectedOptions();
      });
    });

    /**
     * Event listener for form submission.
     * The form submission is intercepted to ensure that a valid variant
     * ID is being submitted to the cart. If no variant ID is available,
     * the submission is blocked, and an error is logged.
     */
    productForm.addEventListener('submit', (event) => {
      event.preventDefault();

      const variantId = productForm.getAttribute('data-variant-id');
      if (!variantId) {
        console.error('No variant selected.');
        return;
      }

      // Data that will be sent to the Shopify cart API
      const cartData = {
        id: variantId,
        quantity: 1, // Assuming quantity is always 1 (adjust if needed)
      };

      // POST request to add the product to cart
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData),
      })
        .then((response) => response.json())
        .then((data) => {
          if (checkoutRedirect) {
            window.location.href = '/checkout';
          } else {
            cart_update(); // Update the cart UI
          }
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    });
  };

  /**
   * Initialize product with configuration data passed from the Liquid template.
   */
  const productConfig = JSON.parse(document.getElementById('product-config').textContent);
  initializeProduct(productConfig);

  /**
   * Re-initialize when the Shopify theme editor changes the section.
   */
  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId === productConfig.sectionId) {
        initializeProduct(productConfig);
      }
    });
  }
});
